import React, { useState } from 'react';
import { ArrowLeft, Plus, Trash2, Edit3, Save, Users, FileText, GripVertical } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from './ui/alert-dialog';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

interface SavedTeam {
  id: string;
  name: string;
  players: string[];
  createdAt: Date;
}

interface TeamsPlayersScreenProps {
  isOpen: boolean;
  onClose: () => void;
  savedTeams: SavedTeam[];
  onSaveTeam: (team: Omit<SavedTeam, 'id' | 'createdAt'>) => void;
  onDeleteTeam: (id: string) => void;
  onUpdateTeam: (id: string, team: Omit<SavedTeam, 'id' | 'createdAt'>) => void;
}

const DragDropTeamPlayerRow = ({
  player,
  index,
  onEdit,
  onRemove,
  onMovePlayer,
}: {
  player: string;
  index: number;
  onEdit: (newName: string) => void;
  onRemove: () => void;
  onMovePlayer: (dragIndex: number, hoverIndex: number) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(player);

  const [{ isDragging }, drag] = useDrag({
    type: 'team-player',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'team-player',
    hover: (draggedItem: { index: number }) => {
      if (draggedItem.index !== index) {
        onMovePlayer(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const handleSave = () => {
    if (editName.trim()) {
      onEdit(editName.trim());
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditName(player);
    setIsEditing(false);
  };

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 cursor-move ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-2 flex-1">
        <GripVertical className="w-4 h-4 text-gray-400" />
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="flex-1 text-base"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                } else if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
              autoFocus
            />
            <Button size="sm" onClick={handleSave}>
              <Save className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        ) : (
          <>
            <span className="font-medium flex-1">{player}</span>
            <div className="flex gap-1">
              <button
                onClick={() => setIsEditing(true)}
                className="text-gray-400 hover:text-blue-500 p-1"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={onRemove}
                className="text-gray-400 hover:text-red-500 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TeamCard = ({
  team,
  onEdit,
  onDelete,
}: {
  team: SavedTeam;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-500" />
        <h4 className="font-medium">{team.name}</h4>
      </div>
      <div className="flex gap-1">
        <button
          onClick={onEdit}
          className="text-gray-400 hover:text-blue-500 p-1 rounded"
        >
          <Edit3 className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          className="text-gray-400 hover:text-red-500 p-1 rounded"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
    
    <div className="space-y-2">
      <p className="text-sm text-gray-600">{team.players.length} players</p>
      <div className="space-y-1">
        {team.players.slice(0, 3).map((player, index) => (
          <p key={index} className="text-sm text-gray-700">{index + 1}. {player}</p>
        ))}
        {team.players.length > 3 && (
          <p className="text-sm text-gray-500">... and {team.players.length - 3} more</p>
        )}
      </div>
    </div>
    
    <p className="text-xs text-gray-500">
      Created {team.createdAt.toLocaleDateString()}
    </p>
  </div>
);

export function TeamsPlayersScreen({
  isOpen,
  onClose,
  savedTeams,
  onSaveTeam,
  onDeleteTeam,
  onUpdateTeam,
}: TeamsPlayersScreenProps) {
  const [activeTab, setActiveTab] = useState('teams');
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<SavedTeam | null>(null);
  
  // Create/Edit team state
  const [teamName, setTeamName] = useState('');
  const [teamPlayers, setTeamPlayers] = useState<string[]>([]);
  const [newPlayerName, setNewPlayerName] = useState('');

  // Player names management
  const [playerNames, setPlayerNames] = useState<string[]>(() => {
    const saved = localStorage.getItem('cricketPlayerNames');
    return saved ? JSON.parse(saved) : [
      'John Smith', 'Michael Johnson', 'David Brown', 'James Wilson',
      'Robert Davis', 'William Miller', 'Richard Moore', 'Thomas Taylor'
    ];
  });
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [newGlobalPlayerName, setNewGlobalPlayerName] = useState('');

  const handleCreateTeam = () => {
    setTeamName('');
    setTeamPlayers([]);
    setNewPlayerName('');
    setShowCreateTeam(true);
  };

  const handleEditTeam = (team: SavedTeam) => {
    setSelectedTeam(team);
    setTeamName(team.name);
    setTeamPlayers([...team.players]);
    setNewPlayerName('');
    setShowEditTeam(true);
  };

  const handleSaveTeam = () => {
    if (teamName.trim() && teamPlayers.length > 0) {
      if (showEditTeam && selectedTeam) {
        onUpdateTeam(selectedTeam.id, {
          name: teamName.trim(),
          players: teamPlayers
        });
        setShowEditTeam(false);
      } else {
        onSaveTeam({
          name: teamName.trim(),
          players: teamPlayers
        });
        setShowCreateTeam(false);
      }
      setTeamName('');
      setTeamPlayers([]);
      setSelectedTeam(null);
    }
  };

  const handleDeleteTeam = () => {
    if (selectedTeam) {
      onDeleteTeam(selectedTeam.id);
      setShowDeleteDialog(false);
      setSelectedTeam(null);
    }
  };

  const handleAddPlayerToTeam = () => {
    if (newPlayerName.trim() && !teamPlayers.includes(newPlayerName.trim())) {
      setTeamPlayers(prev => [...prev, newPlayerName.trim()]);
      setNewPlayerName('');
    }
  };

  const handleRemovePlayerFromTeam = (playerName: string) => {
    setTeamPlayers(prev => prev.filter(p => p !== playerName));
  };

  const handleEditPlayerInTeam = (oldName: string, newName: string) => {
    if (newName.trim()) {
      setTeamPlayers(prev => prev.map(p => p === oldName ? newName.trim() : p));
    }
  };

  const handleMovePlayerInTeam = (dragIndex: number, hoverIndex: number) => {
    setTeamPlayers(prev => {
      const newPlayers = [...prev];
      const draggedPlayer = newPlayers[dragIndex];
      newPlayers.splice(dragIndex, 1);
      newPlayers.splice(hoverIndex, 0, draggedPlayer);
      return newPlayers;
    });
  };

  const handleAddGlobalPlayer = () => {
    if (newGlobalPlayerName.trim() && !playerNames.includes(newGlobalPlayerName.trim())) {
      const updatedNames = [...playerNames, newGlobalPlayerName.trim()].sort();
      setPlayerNames(updatedNames);
      localStorage.setItem('cricketPlayerNames', JSON.stringify(updatedNames));
      setNewGlobalPlayerName('');
      setShowAddPlayer(false);
    }
  };

  const handleDeleteGlobalPlayer = (playerName: string) => {
    const updatedNames = playerNames.filter(p => p !== playerName);
    setPlayerNames(updatedNames);
    localStorage.setItem('cricketPlayerNames', JSON.stringify(updatedNames));
  };

  const handleEditGlobalPlayer = (oldName: string, newName: string) => {
    if (newName.trim()) {
      const updatedNames = playerNames.map(p => p === oldName ? newName.trim() : p).sort();
      setPlayerNames(updatedNames);
      localStorage.setItem('cricketPlayerNames', JSON.stringify(updatedNames));
    }
  };

  const handleAddFromGlobalNames = (playerName: string) => {
    if (!teamPlayers.includes(playerName)) {
      setTeamPlayers(prev => [...prev, playerName]);
    }
  };

  if (!isOpen) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-blue-500" />
            </button>
            <h1 className="text-lg font-semibold">Teams & Players</h1>
          </div>
          <Button
            onClick={activeTab === 'teams' ? handleCreateTeam : () => setShowAddPlayer(true)}
            size="sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            {activeTab === 'teams' ? 'New Team' : 'Add Player'}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-2 mx-4 mt-4">
            <TabsTrigger value="teams" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Saved Teams
            </TabsTrigger>
            <TabsTrigger value="players" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Player Names
            </TabsTrigger>
          </TabsList>

          {/* Teams Tab */}
          <TabsContent value="teams" className="flex-1 overflow-y-auto p-4">
            <div className="max-w-2xl mx-auto">
              {savedTeams.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-medium mb-2">No Saved Teams</h3>
                  <p className="text-sm mb-4">Create your first team to get started</p>
                  <Button onClick={handleCreateTeam}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Team
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {savedTeams.map((team) => (
                    <TeamCard
                      key={team.id}
                      team={team}
                      onEdit={() => handleEditTeam(team)}
                      onDelete={() => {
                        setSelectedTeam(team);
                        setShowDeleteDialog(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Players Tab */}
          <TabsContent value="players" className="flex-1 overflow-y-auto p-4">
            <div className="max-w-md mx-auto">
              <div className="space-y-4">
                <div className="text-center">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <h3 className="font-medium">Common Player Names</h3>
                  <p className="text-sm text-gray-600">
                    Manage a list of common player names for quick team creation
                  </p>
                </div>

                {playerNames.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <p className="mb-4">No player names saved</p>
                    <Button onClick={() => setShowAddPlayer(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Player
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {playerNames.map((player, index) => (
                      <div
                        key={player}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <span className="font-medium">{player}</span>
                        <button
                          onClick={() => handleDeleteGlobalPlayer(player)}
                          className="text-gray-400 hover:text-red-500 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create/Edit Team Modal */}
        {(showCreateTeam || showEditTeam) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">
                  {showEditTeam ? 'Edit Team' : 'Create New Team'}
                </h3>
              </div>
              
              <div className="p-4 flex-1 overflow-y-auto space-y-4">
                {/* Team Name */}
                <div>
                  <Label htmlFor="teamName">Team Name</Label>
                  <Input
                    id="teamName"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Enter team name"
                    className="mt-1"
                  />
                </div>

                {/* Quick Add from Global Names */}
                <div className="space-y-2">
                  <Label>Quick Add Players</Label>
                  <div className="flex flex-wrap gap-2">
                    {playerNames.slice(0, 6).map((player) => (
                      <Button
                        key={player}
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddFromGlobalNames(player)}
                        disabled={teamPlayers.includes(player)}
                        className="text-xs"
                      >
                        {player}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Add Custom Player */}
                <div className="space-y-2">
                  <Label>Add Custom Player</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newPlayerName}
                      onChange={(e) => setNewPlayerName(e.target.value)}
                      placeholder="Enter player name"
                      className="text-base"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAddPlayerToTeam();
                        }
                      }}
                    />
                    <Button 
                      onClick={handleAddPlayerToTeam} 
                      disabled={!newPlayerName.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Team Players */}
                {teamPlayers.length > 0 && (
                  <div className="space-y-2">
                    <Label>Team Players ({teamPlayers.length})</Label>
                    <p className="text-sm text-gray-600">Drag to reorder</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {teamPlayers.map((player, index) => (
                        <DragDropTeamPlayerRow
                          key={`team-${index}-${player}`}
                          player={player}
                          index={index}
                          onEdit={(newName) => handleEditPlayerInTeam(player, newName)}
                          onRemove={() => handleRemovePlayerFromTeam(player)}
                          onMovePlayer={handleMovePlayerInTeam}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-200 flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateTeam(false);
                    setShowEditTeam(false);
                    setTeamName('');
                    setTeamPlayers([]);
                    setSelectedTeam(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveTeam}
                  disabled={!teamName.trim() || teamPlayers.length === 0}
                  className="flex-1"
                >
                  {showEditTeam ? 'Update Team' : 'Create Team'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Player Modal */}
        {showAddPlayer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-lg max-w-sm w-full">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Add Player Name</h3>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <Label htmlFor="globalPlayerName">Player Name</Label>
                  <Input
                    id="globalPlayerName"
                    value={newGlobalPlayerName}
                    onChange={(e) => setNewGlobalPlayerName(e.target.value)}
                    placeholder="Enter player name"
                    className="mt-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddGlobalPlayer();
                      }
                    }}
                    autoFocus
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddPlayer(false);
                    setNewGlobalPlayerName('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddGlobalPlayer}
                  disabled={!newGlobalPlayerName.trim()}
                  className="flex-1"
                >
                  Add Player
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Team Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Team</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedTeam?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteTeam} className="bg-red-600 hover:bg-red-700">
                Delete Team
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DndProvider>
  );
}